import { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { Link as RouterLink } from 'react-router'
import type { User as FirebaseUser } from 'firebase/auth'
import {
  signInWithEmail,
  createAccount,
  sendVerificationEmail,
  sendResetEmail
} from '../../services/authService'
import { createUserDocument } from '../../services/profileService'
import { writeSignupAcknowledgement } from '../../services/acknowledgementService'
import {
  validateEmail,
  validatePasswordLength,
  validatePasswordsMatch,
  mapAuthError
} from '../../utils/authValidation'
import routes from '../../pages/routes'

export type FormMode = 'signIn' | 'createAccount' | 'forgotPassword'

export interface EmailPasswordFormProps {
  initialMode?: FormMode
  onSignInSuccess: (user: FirebaseUser, isNewUser: boolean) => void
  onVerificationRequired: () => void
}

interface SignInFormValues {
  email: string
  password: string
}

interface CreateAccountFormValues {
  email: string
  password: string
  confirmPassword: string
}

interface ForgotPasswordFormValues {
  email: string
}

type FormValues = SignInFormValues &
  Partial<Pick<CreateAccountFormValues, 'confirmPassword'>>

const EmailPasswordForm = ({
  initialMode = 'signIn',
  onSignInSuccess,
  onVerificationRequired
}: EmailPasswordFormProps) => {
  const [mode, setMode] = useState<FormMode>(initialMode)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: ''
    }
  })

  const switchMode = (newMode: FormMode) => {
    const currentEmail = getValues('email')
    setMode(newMode)
    setError(null)
    setSuccessMessage(null)
    setTermsAccepted(false)
    setValue('password', '')
    setValue('confirmPassword', '')
    setValue('email', currentEmail)
  }

  const handleSignIn = async (data: FormValues) => {
    setLoading(true)
    setError(null)
    try {
      const credential = await signInWithEmail(data.email, data.password)
      const skipVerification =
        import.meta.env.VITE_SKIP_EMAIL_VERIFICATION === 'true'
      if (skipVerification || credential.user.emailVerified) {
        onSignInSuccess(credential.user, false)
      } else {
        onVerificationRequired()
      }
    } catch (err: any) {
      setError(mapAuthError(err.code))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAccount = async (data: FormValues) => {
    if (!termsAccepted) {
      setError('You must accept the Terms and Conditions to create an account.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const credential = await createAccount(data.email, data.password)
      await sendVerificationEmail(credential.user)
      const localPart = data.email.split('@')[0]
      await createUserDocument(credential.user.uid, data.email, localPart)
      await writeSignupAcknowledgement(credential.user.uid, data.email)
      setSuccessMessage(
        'Check your email for a verification link to complete your registration.'
      )
    } catch (err: any) {
      setError(mapAuthError(err.code))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (data: FormValues) => {
    setLoading(true)
    setError(null)
    try {
      await sendResetEmail(data.email)
      setSuccessMessage(
        'If an account exists with that email, a reset link has been sent.'
      )
    } catch (err: any) {
      if (
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/invalid-email'
      ) {
        setSuccessMessage(
          'If an account exists with that email, a reset link has been sent.'
        )
      } else {
        setError('Could not send reset email. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data: FormValues) => {
    switch (mode) {
      case 'signIn':
        return handleSignIn(data)
      case 'createAccount':
        return handleCreateAccount(data)
      case 'forgotPassword':
        return handleForgotPassword(data)
    }
  }

  const submitButtonText = () => {
    switch (mode) {
      case 'signIn':
        return 'Sign In'
      case 'createAccount':
        return 'Create Account'
      case 'forgotPassword':
        return 'Send Reset Email'
    }
  }

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 400
      }}
    >
      {error && <Alert severity='error'>{error}</Alert>}
      {successMessage && <Alert severity='success'>{successMessage}</Alert>}

      <TextField
        label='Email'
        type='email'
        fullWidth
        {...register('email', {
          validate: value =>
            validateEmail(value) || 'Please enter a valid email address.'
        })}
        error={!!errors.email}
        helperText={errors.email?.message}
      />

      {mode !== 'forgotPassword' && (
        <TextField
          label='Password'
          type='password'
          fullWidth
          {...register('password', {
            validate: value => {
              if (mode === 'createAccount') {
                return (
                  validatePasswordLength(value) ||
                  'Password must be at least 8 characters.'
                )
              }
              return true
            }
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
        />
      )}

      {mode === 'createAccount' && (
        <TextField
          label='Confirm Password'
          type='password'
          fullWidth
          {...register('confirmPassword', {
            validate: value => {
              const password = watch('password')
              return (
                validatePasswordsMatch(password, value || '') ||
                'Passwords do not match.'
              )
            }
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />
      )}

      {mode === 'createAccount' && (
        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)}
            />
          }
          label={
            <Typography variant='body2'>
              I accept the{' '}
              <Link component={RouterLink} to={routes.terms}>
                Terms & Conditions
              </Link>
            </Typography>
          }
        />
      )}

      <Button
        type='submit'
        variant='contained'
        size='large'
        disabled={loading || (mode === 'createAccount' && !termsAccepted)}
        startIcon={
          loading ? <CircularProgress size={20} color='inherit' /> : undefined
        }
        sx={{ textTransform: 'none' }}
      >
        {submitButtonText()}
      </Button>

      {mode === 'signIn' && (
        <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
          <Link
            component='button'
            type='button'
            variant='body2'
            onClick={() => switchMode('forgotPassword')}
          >
            Forgot password?
          </Link>
          <Typography variant='body2'>
            Don&apos;t have an account?{' '}
            <Link component={RouterLink} to={routes.signup} variant='body2'>
              Create one
            </Link>
          </Typography>
        </Box>
      )}

      {mode === 'createAccount' && (
        <Box display='flex' justifyContent='center'>
          <Typography variant='body2'>
            Already have an account?{' '}
            <Link component={RouterLink} to={routes.signin} variant='body2'>
              Sign in
            </Link>
          </Typography>
        </Box>
      )}

      {mode === 'forgotPassword' && (
        <Box display='flex' justifyContent='center'>
          <Link component={RouterLink} to={routes.signin} variant='body2'>
            Back to sign in
          </Link>
        </Box>
      )}
    </Box>
  )
}

export default EmailPasswordForm
